import { useEffect, useRef } from 'react';
import { EditorChangeType, EditorObserver } from '@src/editor/types';
import { useStore, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useVideoEditorLogging } from './useVideoEditorLogging';
import { useVideoCreatorStore } from '@src/stores-v2';

/**
 * Hook that wires the headless `EditorStore` to the global `VideoSourceStore`.
 *
 *  • On mount it loads any existing `videoSourceStore.source` into the editor
 *    (instant).
 *  • It observes timeline / element changes in the editor and instantly pushes
 *    the translated source to `videoSourceStore.setSource`.
 *  • It reacts to `videoSourceStore.onSourceChange` to immediately sync any
 *    incoming source changes into the editor (unless we were the originator of
 *    the change).
 *
 * The `VideoSourceStore` itself is still responsible for debouncing writes to
 * the renderer, so we purposefully do *not* debounce here.
 */
export function useHeadlessEditorIntegration(
  enableDebugTools: boolean = true,
  enableLogging: boolean = typeof process !== 'undefined' &&
    process.env.NODE_ENV === 'development',
) {
  const { editorStore, videoSourceStore, transcriptionStore } = useStore();
  const videoCreator = useVideoCreatorStore();

  // Set up VideoEditor logging if enabled
  useVideoEditorLogging(
    editorStore?.editor,
    {
      enableCommandLogging: enableLogging,
      enableBatchLogging: enableLogging,
      enablePerformanceLogging: enableLogging && enableDebugTools,
      detailedCommandPerformance: enableDebugTools,
    },
    enableLogging,
  );

  // ---------------------------------------------------------------------------
  // Feature flag – default to `true` for local / dev environments
  // ---------------------------------------------------------------------------
  const { currentRole } = useUserIdentity();
  const { enableHeadlessEditor = false } = useFlagsCombination(currentRole);

  const isLocalChange = useRef(false);
  const editorRef = useRef<typeof editorStore | null>(
    enableHeadlessEditor ? editorStore : null,
  );

  useEffect(() => {
    // If the feature is disabled simply bail out (but keep hook registered!)
    if (!enableHeadlessEditor) {
      // Drop any debug globals from a previous enabled run
      if (enableDebugTools && typeof window !== 'undefined') {
        delete (window as any).__headlessEditor;
        delete (window as any).__videoSourceStore;
      }
      return;
    }

    editorStore.headlessEnabled = true;

    // 1️⃣  Initial load – sync existing source into the editor
    if (videoSourceStore.source?.elements?.length) {
      const projectJson = videoSourceStore.source;
      const originalTranscription =
        transcriptionStore.getOriginalTranscription();
      editorStore.loadProject(
        projectJson,
        originalTranscription?.elements || [],
      );
    }

    // 2️⃣  Headless ➜ Store observer
    const observer: EditorObserver = {
      onEditorChange: (_editor, changeType) => {
        if (
          changeType === EditorChangeType.TIMELINE_CHANGED ||
          changeType === EditorChangeType.ELEMENT_ADDED ||
          changeType === EditorChangeType.ELEMENT_UPDATED ||
          changeType === EditorChangeType.ELEMENT_REMOVED
        ) {
          if (isLocalChange.current) return;

          try {
            isLocalChange.current = true;

            const newSource = editorStore.timeline.toJSON();

            // Lightweight guard – prevent needless writes when the element
            // signature (ids + length) hasn’t changed.
            const prevElements = videoSourceStore.source?.elements ?? [];
            const nextElements = newSource.elements ?? [];

            const sameSignature =
              prevElements.length === nextElements.length &&
              prevElements.every((el: any, idx: number) =>
                el?.id && nextElements[idx]?.id
                  ? el.id === nextElements[idx].id
                  : true,
              );

            if (!sameSignature) {
              videoSourceStore.setSource(newSource);
            } else {
              // Even if signature is the same, timing or other props may have
              // changed – still push the update for simplicity.
              videoSourceStore.setSource(newSource);
            }
          } finally {
            isLocalChange.current = false;
          }
        }
      },
    };

    editorStore.editor.registerObserver(observer);

    // 3️⃣  Store ➜ Headless reaction
    const disposeReaction = videoSourceStore.onSourceChange((src) => {
      if (isLocalChange.current) return;

      try {
        isLocalChange.current = true;

        const projectJson = src as any; // Convert as object.
        editorStore.editor.syncFromRenderer(projectJson);
        editorStore.refreshTimeline();
      } finally {
        isLocalChange.current = false;
      }
    });
    const disposeReaction2 = videoCreator.onExtraElementDataChange(
      (extraElementData) => {
        editorStore.editor.passExtraElementData(extraElementData);
      },
    );

    // 4️⃣  Debug helpers (window globals)
    if (enableDebugTools && typeof window !== 'undefined') {
      (window as any).__headlessEditor = editorStore;
      (window as any).__videoSourceStore = videoSourceStore;
    }

    // 🧹  Cleanup
    return () => {
      editorStore.editor.unregisterObserver(observer);
      disposeReaction();
      disposeReaction2();

      if (enableDebugTools && typeof window !== 'undefined') {
        delete (window as any).__headlessEditor;
        delete (window as any).__videoSourceStore;
      }
    };

    // We intentionally ignore dependencies that would recreate observers on
    // every render.  The stores themselves behave like singletons in the
    // current React tree.
  }, [enableHeadlessEditor, videoSourceStore.source?.elements]);

  return editorRef;
}
